import { useRef } from "react";
import { Download, Eye, ArrowRightLeft, Loader2, Lock, Link, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FileIcon from "@/assets/icons/file.svg?react";
import { ExpeditionDocumentFileBoDto, ExpeditionDocumentFileDto } from "@/types/api/api-types";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useFileTypeLabels } from "@/hooks/useEnumLabels";

type DocumentVariant = {
  file: ExpeditionDocumentFileDto | ExpeditionDocumentFileBoDto;
  label: string;
};

type SingleDocumentUploadCardProps = {
  file?: ExpeditionDocumentFileDto | ExpeditionDocumentFileBoDto;
  alternateFormats?: DocumentVariant[];
  emptyMessage?: string;
  uploadLabel?: string;
  accept?: string;
  isUploading?: boolean;
  onOpen?: (path: string) => void;
  onDownload?: (file: ExpeditionDocumentFileDto | ExpeditionDocumentFileBoDto) => void;
  onFileSelect?: (file: File) => Promise<void>;
  hideDetailsInfo?: boolean;
};

export const SingleDocumentUploadCard = ({
  file,
  alternateFormats,
  emptyMessage,
  uploadLabel,
  accept,
  isUploading = false,
  onOpen,
  onDownload,
  onFileSelect,
  hideDetailsInfo = false,
}: SingleDocumentUploadCardProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const fileTypeLabels = useFileTypeLabels();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const selected = e.target.files?.[0];
      if (selected) await onFileSelect?.(selected);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch {
      toast.error(t("singleDocumentUploadCard.replaceError"));
    }
  };

  const hasAlternates = alternateFormats && alternateFormats.length > 0;

  return (
    <>
      {file ? (
        <div className="flex items-center gap-3 rounded-lg border bg-gray-50 p-4">
          <div className="shrink-0 rounded-full border p-2">
            <FileIcon className="size-4" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="truncate text-sm font-medium text-gray-800">{file.name}</p>
            {!hideDetailsInfo && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="lightGray">
                  {fileTypeLabels[file.type as FileTypeEnum] ?? file.type}
                </Badge>
                <span className="text-xs text-gray-500">
                  {new Date(file.createdAt).toLocaleDateString("fr-FR")}
                </span>
                {"isPrivate" in file && file.isPrivate && (
                  <Badge variant="outline" className="gap-1 text-xs">
                    <Lock className="size-3" /> {t("singleDocumentUploadCard.privateBadge")}
                  </Badge>
                )}
                {"sendViaApi" in file && file.sendViaApi && (
                  <Badge variant="outline" className="gap-1 text-xs">
                    <Link className="size-3" /> API
                  </Badge>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            {onOpen && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                type="button"
                title={t("singleDocumentUploadCard.open")}
                onClick={() => onOpen(file.path)}
              >
                <Eye className="size-4" />
              </Button>
            )}

            {onDownload && hasAlternates ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    type="button"
                    title={t("singleDocumentUploadCard.download")}
                  >
                    <Download className="size-4" />
                    <ChevronDown className="size-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {alternateFormats.map((variant) => (
                    <DropdownMenuItem
                      key={variant.file.id}
                      onClick={() =>
                        variant.file.type.includes("EXCEL") && onOpen
                          ? onOpen(variant.file.path)
                          : onDownload(variant.file)
                      }
                    >
                      {variant.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : onDownload ? (
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                type="button"
                title={t("singleDocumentUploadCard.download")}
                onClick={() => onDownload(file)}
              >
                <Download className="size-4" />
              </Button>
            ) : null}

            {onFileSelect && (
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                type="button"
                title={t("singleDocumentUploadCard.replace")}
                disabled={isUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ArrowRightLeft className="size-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-6">
          <p className="text-sm text-gray-500">
            {emptyMessage ?? t("singleDocumentUploadCard.empty")}
          </p>
          {onFileSelect && (
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {isUploading ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <ArrowRightLeft className="mr-2 size-4" />
              )}
              {uploadLabel ?? t("singleDocumentUploadCard.upload")}
            </Button>
          )}
        </div>
      )}

      {onFileSelect && (
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFileChange}
        />
      )}
    </>
  );
};
